import { Link } from "react-router-dom";
import SocialBar from "../SocialBar/SocialBar";
import { Button, Text, Card, Skeleton } from "@chakra-ui/react";
import { ViewHeader } from "../../components/ViewStyle/ViewStyle";
import { SocialIcons } from "../../components/SocialIcons/SocialIcons";
import styled from "styled-components/macro";
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import advanced from "dayjs/plugin/advancedFormat";
import useDigestQuery from "../../hooks/useDigestQuery";
interface IConfig {
  dateFormat: any;
  divideColor: string;
  buttonTextColor: string;
  buttonBgColor: string;
  displayDate: boolean;
  bgColor: string;
  socialBar: boolean;
  socialBarName: string;
  backgroundImageURL: string;
  rgba1: string;
  rgba2: string;
  backgroundSize: string;
}

interface IViewData {
  feature: string;
  type: "newsDigest";
  config: IConfig;
  title: string;
}

interface INewsDigest {
  viewData: IViewData;
  manifestData: any;
  anchorID: string;
}

interface DigestImage {
  small?: string;
  large?: string;
  "aria-label": string | undefined;
}

interface BlogID {
  $id: string;
}

interface BlogDate {
  sec: number;
  usec: number;
}

interface ICalendar {
  epoch: number;
  type: 4;
  feedID: number;
  origin: number;
  eventID: string;
  calendarID: string;
  calendarTitle: string;
  title: string;
  content: string;
  summary: string;
  images: DigestImage[];
}

interface ITwitter {
  epoch: number;
  type: 2;
  feedID: number;
  origin: number;
  /**
   * The @ of the twitter user
   */
  title: string;
  /**
   * Content of twitter post. Can contain HTML prefer to use summary if possible
   */
  content: string;
  /**
   * Summary of the twitter post. Does not contain HTML.
   * Cuts off after certain amount of characters.
   */
  summary: string;
  images: DigestImage[];
  /**
   * Profile image of twitter user. Used if tweet does not contain images.
   */
  profile_image_url_https: string;
  /**
   * ID of specific post. Append to link after the @ for the twitter
   * user (remove @ symbol) and add /status/ between the two. EX: https://twitter.com/ocvtwitter/status/12341235145413
   */
  id_str: string;
  link: string;
}

interface IFacebook {
  epoch: number;
  type: 3;
  feedID: number;
  origin: number;
  /**
   * The PageID and PostID are contained in this key. UserID is before
   * underscore. PostID is after underscore.
   * EX: 109290340297987_0190234578001293. Use like this: https://facebook.com/109290340297987_0190234578001293
   */
  facebookPostID: string;
  /**
   * Facebook page/user title
   */
  title: string;
  /**
   * Content of twitter post. Can contain HTML prefer to use summary if possible
   */
  content: string;
  /**
   * Summary of the twitter post. Does not contain HTML.
   * Cuts off after certain amount of characters.
   */
  summary: string;
  /**
   * Profile image of facebook page. Used if there are no images in the post.
   */
  fb_profile_image: string;
  status_type: string;
  images: DigestImage[];
}

interface IBlog {
  _id: BlogID;
  /**
   * Email of blog post creator.
   */
  creator: string;
  date: BlogDate;
  /**
   * Title of blog post.
   */
  title: string;
  /**
   * Content of blog post. Can contain HTML prefer to use summary.
   */
  content: string;
  /**
   * Different ID than the _id: { $id: number }
   */
  blogID: number;
  images: DigestImage[];
  status: number;
  epoch: number;
  type: 1;
  feedID: number;
  origin: number;
  /**
   * Summary of blog post
   */
  summary: string;
  /**
   * Title of the blog that this post came from
   */
  blogTitle: string;
  blogIdentifier: string;
}

interface IPush {
  epoch: number;
  type: 5;
  feedID: number;
  origin: number;
  /**
   * Title of push notification
   */
  title: string;
  channel: string;
  /**
   * Title of channel for push notification.
   */
  channelTitle: string;
  /**
   * Content of twitter post. Can contain HTML prefer to use summary if possible
   */
  content: string;
  /**
   * Summary of the twitter post. Does not contain HTML.
   * Cuts off after certain amount of characters.
   */
  summary: string;
  images: DigestImage[];
}

interface IRtjb {
  epoch: number;
  type: 6;
  feedID: number;
  origin: number;
  /**
   * Title of rtjb feed
   */
  title: string;
  content: string;
  summary: string;
  images: DigestImage[];
}
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  overflow: hidden;
  gap: 2rem;
  justify-content: center;
  @media (max-width: 1430px) {
    grid-template-columns: repeat(2, minmax(0, 2fr));
  }
  @media (max-width: 945px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
`;
export const HeaderTextDiv = styled.div`
  padding-left: 0.75rem;
  display: flex;
  flex-direction: column;
`;

dayjs.extend(tz);
dayjs.extend(advanced);

export default function NewsDigest({
  viewData,
  anchorID,
  manifestData,
}: INewsDigest): JSX.Element {
  const config = manifestData?.["views"]["newsDigest"]["config"];
  const digestUrl = manifestData?.features[viewData["feature"]]?.["url"];
  // Query for News Digest
  const { data, isLoading, isError, isFetching } = useDigestQuery({
    digestUrl,
    anchorID,
  });
  const callFrom = "newsDigest";
  const digest = data;
  if (isLoading) {
    return (
      <Skeleton
        startColor="black"
        height="20px"
        width="100%"
      />
    );
  }
  if (isFetching) {
    return (
      <Skeleton
        startColor="blue"
        height="20px"
        width="100%"
      />
    );
  }
  if (isError) {
    return (
      <Skeleton
        startColor="red"
        height="20px"
        width="100%"
      />
    );
  }

  let backgroundImageConfig = undefined;

  if (config && config.rgba1 && config.rgba2 && config.backgroundImageURL)
    backgroundImageConfig = `linear-gradient(${config.rgba1}, ${config.rgba2}), url(${config.backgroundImageURL})`;
  else if (config && config.rgba1 && config.rgba2)
    backgroundImageConfig = `linear-gradient(${config.rgba1}, ${config.rgba2})`;
  else if (config && config.backgroundImageURL)
    backgroundImageConfig = `url(${config.backgroundImageURL})`;

  return (
    <section
      id={anchorID}
      data-testid={"newsDigestSection"}
      style={{
        backgroundColor:
          viewData && viewData.config.bgColor
            ? viewData.config.bgColor
            : "#eee",
        backgroundImage: backgroundImageConfig,
        backgroundSize: viewData.config.backgroundSize
          ? viewData.config.backgroundSize
          : "cover",
        display: "flex",
        flexDirection: "column",
        placeItems: "center",
        width: "100%",
        height: "100%",
        gap: "1rem",
        padding: "2rem 0rem",
      }}
    >
      <ViewHeader titleColor={config?.titleColor}>{viewData?.title}</ViewHeader>
      {viewData?.config?.socialBar && (
        <SocialBar
          key="SocialBar in NewsDigest"
          callFrom={callFrom}
          viewData={manifestData?.views[viewData?.config?.socialBarName]}
        />
      )}
      <Grid>
        {digest
          ?.slice(0, 6)
          .sort((a: any, b: any) => b.epoch - a.epoch)
          .map(
            (
              item: ITwitter | IFacebook | IBlog | IPush | ICalendar | IRtjb
            ) => (
              <div
                key={
                  item?.type === 1
                    ? item?._id?.$id
                    : item?.type === 2
                    ? item?.id_str
                    : item?.type === 3
                    ? item?.facebookPostID
                    : item?.type === 4
                    ? item?.eventID
                    : item?.epoch
                }
              >
                <Link
                  to={`/${viewData?.feature.replace(/\s+/g, "")}/${
                    item?.type === 1
                      ? item?._id?.$id
                      : item?.type === 2
                      ? item?.id_str
                      : item?.type === 3
                      ? item?.facebookPostID
                      : item?.type === 4
                      ? item?.eventID
                      : item?.epoch
                  }`}
                  style={{ height: "100%" }}
                >
                  <Card
                    overflow="hidden"
                    variant="filled"
                    bg="white"
                    width="426px"
                    borderRadius="10px"
                    sx={{
                      "@media(max-width: 427px)": {
                        width: "250px",
                      },
                    }}
                  >
                    {/* Image */}
                    <div
                      style={{
                        paddingTop: "0.75rem",
                      }}
                    >
                      {item?.images?.length > 0 ? (
                        item?.images[0]?.large ? (
                          <div
                            role="img"
                            aria-label="Image in Digest"
                            style={{
                              backgroundImage: `url(${item.images[0].large})`,
                              backgroundPosition: "center",
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "contain",
                              height: "15rem",
                            }}
                          ></div>
                        ) : (
                          <>
                            <div
                              role="img"
                              aria-label="Image in Digest"
                              style={{
                                backgroundImage: `url(${item?.images[0].small})`,
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "contain",
                                height: "15rem",
                              }}
                            ></div>
                          </>
                        )
                      ) : (
                        <div>
                          {item.type === 2 && (
                            <>
                              <div
                                role="img"
                                aria-label="Profile Image for Twitter"
                                style={{
                                  backgroundImage: `url(${item?.profile_image_url_https.replace(
                                    "_normal",
                                    ""
                                  )})`,
                                  backgroundPosition: "center",
                                  backgroundRepeat: "no-repeat",
                                  backgroundSize: "contain",
                                  height: "15rem",
                                }}
                              ></div>
                            </>
                          )}
                          {item.type === 3 && (
                            <>
                              <div
                                role="img"
                                aria-label="Profile Image for Facebook"
                                style={{
                                  backgroundImage: `url(${item?.fb_profile_image})`,
                                  backgroundPosition: "center",
                                  backgroundRepeat: "no-repeat",
                                  backgroundSize: "contain",
                                  height: "15rem",
                                }}
                              ></div>
                            </>
                          )}
                          {item.type === 4 && (
                            <>
                              <div
                                role="img"
                                aria-label={
                                  manifestData?.stylesheet?.images?.favicon
                                    ?.altText
                                }
                                style={{
                                  backgroundImage: `url(${manifestData?.stylesheet?.images?.favicon?.url})`,
                                  backgroundPosition: "center",
                                  backgroundRepeat: "no-repeat",
                                  backgroundSize: "contain",
                                  height: "15rem",
                                }}
                              ></div>
                            </>
                          )}
                          {item.type === 1 && (
                            <>
                              <div
                                role="img"
                                aria-label={
                                  manifestData?.stylesheet?.images?.favicon
                                    ?.altText
                                }
                                style={{
                                  backgroundImage: `url(${manifestData?.stylesheet?.images?.favicon?.url})`,
                                  backgroundPosition: "center",
                                  backgroundRepeat: "no-repeat",
                                  backgroundSize: "contain",
                                  height: "15rem",
                                }}
                              ></div>
                            </>
                          )}
                          {item.type === 5 && (
                            <>
                              <div
                                role="img"
                                aria-label={
                                  manifestData?.stylesheet?.images?.favicon
                                    ?.altText
                                }
                                style={{
                                  backgroundImage: `url(${manifestData?.stylesheet?.images?.favicon?.url})`,
                                  backgroundPosition: "center",
                                  backgroundRepeat: "no-repeat",
                                  backgroundSize: "contain",
                                  height: "15rem",
                                }}
                              ></div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Title */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        position: "relative",
                        padding: "0.25rem",
                        alignItems: "center",
                        margin: "0.75rem",
                      }}
                    >
                      {item.type === 1 && SocialIcons("newsDigest", "60px")}
                      {item.type === 2 && SocialIcons("twitterCircle", "60px")}
                      {item.type === 3 && SocialIcons("facebookCircle", "60px")}
                      {item.type === 4 && SocialIcons("calendar2", "60px")}
                      {item.type === 5 && SocialIcons("bell", "60px")}
                      {item.type === 6 && SocialIcons("newsDigest", "60px")}
                      {item.type === 1 && (
                        <HeaderTextDiv>
                          <Text as="b">{item.blogTitle}</Text>
                          {viewData?.config?.displayDate && (
                            <Text
                              fontSize="sm"
                              as="em"
                            >
                              {viewData?.config?.dateFormat
                                ? dayjs
                                    .unix(item?.epoch)
                                    .format(`${viewData?.config?.dateFormat}`)
                                    .toString()
                                : dayjs
                                    .unix(item?.epoch)
                                    .format("MMM DD, YYYY - hh:mm A z")
                                    .toString()}
                            </Text>
                          )}
                        </HeaderTextDiv>
                      )}
                      {item.type === 2 && (
                        <HeaderTextDiv>
                          <Text as="b">Twitter</Text>
                          <Text>{item.title}</Text>

                          {viewData?.config?.displayDate && (
                            <Text
                              fontSize="sm"
                              as="em"
                            >
                              {viewData?.config?.dateFormat
                                ? dayjs
                                    .unix(item?.epoch)
                                    .format(`${viewData?.config?.dateFormat}`)
                                    .toString()
                                : dayjs
                                    .unix(item?.epoch)
                                    .format("MMM DD, YYYY - hh:mm A z")
                                    .toString()}
                            </Text>
                          )}
                        </HeaderTextDiv>
                      )}
                      {item.type === 3 && (
                        <HeaderTextDiv>
                          <Text as="b">Facebook</Text>
                          <Text>{item.title}</Text>

                          {viewData?.config?.displayDate && (
                            <Text
                              fontSize="sm"
                              as="em"
                            >
                              {viewData?.config?.dateFormat
                                ? dayjs
                                    .unix(item?.epoch)
                                    .format(`${viewData?.config?.dateFormat}`)
                                    .toString()
                                : dayjs
                                    .unix(item?.epoch)
                                    .format("MMM DD, YYYY - hh:mm A z")
                                    .toString()}
                            </Text>
                          )}
                        </HeaderTextDiv>
                      )}
                      {item.type === 4 && (
                        <HeaderTextDiv>
                          <Text
                            as="b"
                            casing="capitalize"
                          >
                            {item.calendarID}
                          </Text>
                          {viewData?.config?.displayDate && (
                            <Text
                              fontSize="sm"
                              as="em"
                            >
                              {viewData?.config?.dateFormat
                                ? dayjs
                                    .unix(item?.epoch)
                                    .format(`${viewData?.config?.dateFormat}`)
                                    .toString()
                                : dayjs
                                    .unix(item?.epoch)
                                    .format("MMM DD, YYYY - hh:mm A z")
                                    .toString()}
                            </Text>
                          )}
                        </HeaderTextDiv>
                      )}
                      {item.type === 5 && (
                        <HeaderTextDiv>
                          <Text as="b">Notification</Text>
                          <Text>{item.channelTitle}</Text>

                          {viewData?.config?.displayDate && (
                            <Text
                              fontSize="sm"
                              as="em"
                            >
                              {viewData?.config?.dateFormat
                                ? dayjs
                                    .unix(item?.epoch)
                                    .format(`${viewData?.config?.dateFormat}`)
                                    .toString()
                                : dayjs
                                    .unix(item?.epoch)
                                    .format("MMM DD, YYYY - hh:mm A z")
                                    .toString()}
                            </Text>
                          )}
                        </HeaderTextDiv>
                      )}
                      {item.type === 6 && (
                        <HeaderTextDiv>
                          <Text as="b">{item.title}</Text>
                          {viewData?.config?.displayDate && (
                            <Text
                              fontSize="sm"
                              as="em"
                            >
                              {viewData?.config?.dateFormat
                                ? dayjs
                                    .unix(item?.epoch)
                                    .format(`${viewData?.config?.dateFormat}`)
                                    .toString()
                                : dayjs
                                    .unix(item?.epoch)
                                    .format("MMM DD, YYYY - hh:mm A z")
                                    .toString()}
                            </Text>
                          )}
                        </HeaderTextDiv>
                      )}
                    </div>
                    <hr />
                    {/* Content */}
                    <div
                      style={{
                        display: "flex",
                        paddingLeft: "0.75rem",
                        paddingRight: "0.75rem",
                        marginBottom: "0.75rem",
                        textAlign: "left",
                        flexDirection: "column",
                        margin: "0.75rem",
                      }}
                    >
                      {item.type === 1 && (
                        <>
                          <Text as="b">{item.title}</Text>
                          <p
                            dangerouslySetInnerHTML={{
                              __html:
                                item.summary.length > 198
                                  ? `${item?.summary}...`
                                  : item?.summary,
                            }}
                          ></p>
                        </>
                      )}
                      {item.type === 2 && (
                        <p
                          dangerouslySetInnerHTML={{
                            __html: item.summary,
                          }}
                        ></p>
                      )}
                      {item.type === 3 && (
                        <p
                          dangerouslySetInnerHTML={{
                            __html:
                              item.summary.length > 198
                                ? `${item?.summary}...`
                                : item?.summary,
                          }}
                        ></p>
                      )}
                      {item.type === 4 && (
                        <>
                          <Text as="b">{item.title}</Text>
                          <p
                            dangerouslySetInnerHTML={{
                              __html: item.content,
                            }}
                          ></p>
                        </>
                      )}
                      {item.type === 5 && (
                        <>
                          <Text as="b">{item.title}</Text>
                          <p
                            dangerouslySetInnerHTML={{
                              __html:
                                item.summary.length > 198
                                  ? `${item?.summary}...`
                                  : item?.summary,
                            }}
                          ></p>
                        </>
                      )}
                      {item.type === 6 && (
                        <>
                          <Text as="b">{item.title}</Text>
                          <p
                            dangerouslySetInnerHTML={{
                              __html:
                                item.summary.length > 198
                                  ? `${item?.summary}...`
                                  : item?.summary,
                            }}
                          ></p>
                        </>
                      )}
                    </div>
                  </Card>
                </Link>
              </div>
            )
          )}
      </Grid>
      <Link to={`/${viewData?.feature.replace(/\s+/g, "")}`}>
        <Button
          mt="1rem"
          size="lg"
          bg={viewData?.config?.buttonBgColor}
          color={viewData?.config?.buttonTextColor}
        >
          {config?.buttonText.toUpperCase()}
        </Button>
      </Link>
    </section>
  );
}
